import React, { useEffect, useState } from 'react';
import { Grid, DialogContent, DialogTitle, CircularProgress, Typography, Button, Chip } from '@mui/material';
import { SettingsRounded, CheckCircleOutlineRounded, HighlightOffRounded } from '@mui/icons-material';
import { NotificationAddRounded, NotificationsRounded, DeleteOutlineRounded } from '@mui/icons-material';
import { RoundedDialog, StandardDialogActions, UserChip } from '../Components';
import { FormatDate, highlightElementError } from '../Functions';
import StateManager from '../StateManager';
import { isEmpty, isArray, isNumber } from 'lodash';
import { green, grey, red } from '@mui/material/colors';
import ReminderSettings from './ReminderSettings';
import axios from 'axios';

const EntryRemindersDialog = ({ sourceType, sourceId, editPermission, onClose, open, entryCompleted }) => {
  const [loading, setLoading] = useState(false);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [sentReminders, setSentReminders] = useState([]);
  const [showMoreReminders, setShowMoreReminders] = useState(false);
  const [sentEscalations, setSentEscalations] = useState([]);
  const [showMoreEscalations, setShowMoreEscalations] = useState(false);
  const [reminderSettingsDialog, setReminderSettingsDialog] = useState(null);
  const [sending, setSending] = useState(false);

  const loadSettings = sourceId && open;

  const SHOW_LIMIT = 3;
  const shownReminders = showMoreReminders ? sentReminders : sentReminders.filter((_x, i) => i < SHOW_LIMIT);
  const shownEscalations = showMoreEscalations ? sentEscalations : sentEscalations.filter((_x, i) => i < SHOW_LIMIT);

  useEffect(() => {
    // Fetch reminder settings for the source
    if (!loadSettings || !sourceId) return;

    setLoading(true);
    axios
      .get(`/reminders/getReminderInfoForEntry`, { params: { sourceId } })
      .then(({ data }) => {
        if (data) {
          setReminderSettings(data.settings);
          setSentReminders(data.sentReminders);
          setSentEscalations(data.sentEscalations);
        } else {
          setReminderSettings(null);
          setSentReminders([]);
          setSentEscalations([]);
        }
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [loadSettings, sourceId]);

  function sendReminderNow() {
    setSending(true);
    axios
      .post(`/reminders/sendOneOffReminderForEntry`, { sourceId, sourceType })
      .then(({ data }) => {
        if (data) {
          setSentReminders([data, ...sentReminders]);
          StateManager.setSuccessAlert('Reminder has been sent');
        } else {
          StateManager.setWarningAlert('Failed to send the reminder');
        }
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      })
      .finally(() => {
        setSending(false);
      });
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reminders</DialogTitle>
      <DialogContent>
        {loading ? (
          <Grid container alignItems={'center'} justifyContent={'center'} sx={{ height: '15vh' }}>
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container alignContent={'baseline'} sx={{ minHeight: '15vh' }}>
            <Grid container alignItems={'center'}>
              {reminderSettings ? (
                <CheckCircleOutlineRounded fontSize="large" sx={{ color: green[500] }} />
              ) : (
                <HighlightOffRounded fontSize="large" sx={{ color: grey[500] }} />
              )}
              <Typography variant="subtitle1" sx={{ ml: 1, color: (theme) => theme.palette.text.secondary }}>
                {reminderSettings ? 'Reminders have been set up' : 'No reminders have been set up'}
              </Typography>
            </Grid>

            {editPermission && !entryCompleted && (
              <Grid container sx={{ mt: 2 }}>
                <Button
                  startIcon={<SettingsRounded />}
                  variant="outlined"
                  onClick={() => setReminderSettingsDialog(true)}
                >
                  {reminderSettings ? 'change reminder settings' : 'set up reminders'}
                </Button>

                <ReminderSettingsDialog
                  sourceId={sourceId}
                  sourceType={sourceType}
                  open={reminderSettingsDialog}
                  onClose={() => setReminderSettingsDialog(false)}
                  initial={reminderSettings}
                  onResult={(res) => setReminderSettings(res)}
                />
              </Grid>
            )}

            <Grid container sx={{ mt: 2, mb: 1 }}>
              <Typography sx={{ fontWeight: 500 }} variant="subtitle1">
                Sent reminders{!isEmpty(sentReminders) ? ` (${sentReminders.length})` : ''}:
              </Typography>

              {!entryCompleted && (
                <Button
                  startIcon={sending ? <CircularProgress size={24} /> : <NotificationAddRounded />}
                  variant="outlined"
                  onClick={sendReminderNow}
                  sx={{ ml: 'auto' }}
                >
                  send reminder now
                </Button>
              )}
            </Grid>

            <Grid container>
              {isArray(shownReminders) && !isEmpty(shownReminders) ? (
                <>
                  <Grid container>
                    {shownReminders.map((reminder) => (
                      <Grid container key={reminder._id} sx={{ borderTop: `1px solid ${grey[300]}` }}>
                        <Grid container sx={{ mt: 0.5 }}>
                          <NotificationsRounded sx={{ mr: 1, color: grey[500] }} />

                          <Typography sx={{ color: (theme) => theme.palette.text.secondary }}>
                            {FormatDate(reminder.createdAt)}
                          </Typography>

                          {reminder.sentManually && (
                            <Chip sx={{ ml: 'auto' }} color="primary" size="small" label={'Sent manually'} />
                          )}
                        </Grid>
                        {!isEmpty(reminder.userIds) && (
                          <Grid container alignItems={'center'}>
                            <Typography sx={{ mr: 1 }}>Notification sent to:</Typography>
                            {reminder.userIds.map((userId, i) => (
                              <UserChip size="small" key={i} id={userId} />
                            ))}
                          </Grid>
                        )}
                        {!isEmpty(reminder.emails) && (
                          <Grid container alignItems={'center'}>
                            <Typography sx={{ mr: 1 }}>Emailed to:</Typography>
                            {reminder.emails.map((email, i) => (
                              <Chip size="small" key={i} label={email} sx={{ mr: 1, my: 1 }} />
                            ))}
                          </Grid>
                        )}
                      </Grid>
                    ))}
                  </Grid>

                  {sentReminders.length > SHOW_LIMIT && (
                    <Grid container>
                      <Button variant="outlined" onClick={() => setShowMoreReminders(!showMoreReminders)}>
                        Show {showMoreReminders ? 'less' : 'more'}
                      </Button>
                    </Grid>
                  )}
                </>
              ) : (
                <Typography gutterBottom sx={{ color: (theme) => theme.palette.text.secondary }}>
                  No reminders have been sent
                </Typography>
              )}
            </Grid>

            {editPermission && (
              <>
                <Grid container sx={{ mt: 2, mb: 1 }}>
                  <Typography sx={{ fontWeight: 500 }} variant="subtitle1">
                    Sent escalation messages{!isEmpty(sentEscalations) ? ` (${sentEscalations.length})` : ''}:
                  </Typography>
                </Grid>

                <Grid container>
                  {isArray(shownEscalations) && !isEmpty(shownEscalations) ? (
                    <>
                      <Grid container>
                        {shownEscalations.map((escalation) => (
                          <Grid container key={escalation._id} sx={{ borderTop: `1px solid ${grey[300]}` }}>
                            <Grid container>
                              <Typography sx={{ color: (theme) => theme.palette.text.secondary }}>
                                {FormatDate(escalation.createdAt)}
                              </Typography>
                            </Grid>
                            {!isEmpty(escalation.userIds) && (
                              <Grid container alignItems={'center'}>
                                <Typography sx={{ mr: 1 }}>Notification sent to:</Typography>
                                {escalation.userIds.map((userId, i) => (
                                  <UserChip size="small" key={i} id={userId} />
                                ))}
                              </Grid>
                            )}
                            {!isEmpty(escalation.emails) && (
                              <Grid container alignItems={'center'}>
                                <Typography sx={{ mr: 1 }}>Emailed to:</Typography>
                                {escalation.emails.map((email, i) => (
                                  <Chip size="small" key={i} label={email} sx={{ mr: 1, my: 1 }} />
                                ))}
                              </Grid>
                            )}
                          </Grid>
                        ))}
                      </Grid>

                      {sentEscalations.length > SHOW_LIMIT && (
                        <Grid container>
                          <Button variant="outlined" onClick={() => setShowMoreEscalations(!showMoreEscalations)}>
                            Show {showMoreEscalations ? 'less' : 'more'}
                          </Button>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Typography gutterBottom sx={{ color: (theme) => theme.palette.text.secondary }}>
                      No escalation messages have been sent
                    </Typography>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        )}
      </DialogContent>
      <StandardDialogActions onClose={onClose} />
    </RoundedDialog>
  );
};

const ReminderSettingsDialog = ({ onClose, open, initial, onResult, sourceType, sourceId }) => {
  const [reminderSettings, setReminderSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setReminderSettings(initial);
  }, [initial]);

  function done() {
    // check reminder settings are vaild
    const hasUpcomingSettings = !isEmpty(reminderSettings.upcomingSettings?.occurrences);
    const hasOverdueSettings =
      !isEmpty(reminderSettings.overdueSettings?.occurrences) ||
      (reminderSettings.overdueSettings?.sendRepetitively && isNumber(reminderSettings.overdueSettings?.interval));
    const hasEscalationSettings = !isEmpty(reminderSettings.overdueSettings?.occurrences);

    if (!hasUpcomingSettings && !hasOverdueSettings && !hasEscalationSettings) {
      StateManager.setErrorAlert('Provide at least one date when the reminder should be sent');
      return;
    }

    saveSettings(reminderSettings);
  }

  function saveSettings(settings) {
    setSaving(true);
    axios
      .post(`/reminders/saveReminderSettingsForSource`, { sourceId, sourceType, settings })
      .then(({ data }) => {
        onResult(data);
        onClose();
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      })
      .finally(() => {
        setSaving(false);
      });
  }

  function confirmSetttingsDelete() {
    StateManager.setConfirm('You are about to delete the reminder settings', () => saveSettings(null));
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Reminders setting</DialogTitle>
      <DialogContent>
        <ReminderSettings
          initial={reminderSettings}
          onChange={(value) => setReminderSettings(value)}
          sourceType={sourceType}
          defaultEnabled
        />
      </DialogContent>
      <StandardDialogActions
        onClose={onClose}
        onDone={done}
        saving={saving}
        additionalActions={
          initial && (
            <Button
              sx={{ color: red[500], mr: 'auto' }}
              onClick={confirmSetttingsDelete}
              startIcon={<DeleteOutlineRounded />}
            >
              delete reminder
            </Button>
          )
        }
      />
    </RoundedDialog>
  );
};

export default EntryRemindersDialog;
